import React, {
  Fragment,
  StrictMode,
  useEffect,
  useRef,
  useState,
} from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { domAnimation, LazyMotion } from "framer-motion";
import { queryClient } from "@common/http/query-client";
import { SiteConfigContext } from "@common/core/settings/site-config-context";
import { SiteConfig } from "@app/site-config";
import deepMerge from "deepmerge";
import { BaseSiteConfig } from "@common/core/settings/base-site-config";
import { ThemeProvider } from "@common/core/theme-provider";
import { AppearanceListener } from "@common/admin/appearance/commands/appearance-listener";
import { CookieNotice } from "@common/ui/cookie-notice/cookie-notice";
import { ToastContainer } from "@ui/toast/toast-container";
import { DialogStoreOutlet } from "@ui/overlays/store/dialog-store-outlet";
import { StackOutlet } from "@ui/overlays/stack-navigator/stack-outlet";
import type { Router } from "@remix-run/router/dist/router";
import { useSettings } from "@ui/settings/use-settings";
import { useAuth } from "@common/auth/use-auth";
import { verifyEmailRouter } from "@common/auth/ui/email-verification-page/email-verification-page";
import {
  Outlet,
  RouterProvider,
  useLocation,
  useNavigation,
  useRouteError,
} from "react-router-dom";
import { FullPageLoader } from "@ui/progress/full-page-loader";
import { TopProgressBar } from "@ui/progress/top-progress-bar";
import { NotFoundPage } from "@common/ui/not-found-page/not-found-page";
import { PageErrorMessage } from "@common/errors/page-error-message";
import { DialogStackProvider } from "@ui/overlays/dialog/dialog-stack";
import { AppBrowser } from "./capacitor/app-browser";
import { appPushNotificationManager } from "./capacitor/app-push-notifications-manager";
import { AlbumTaxonomiesProvider } from "@common/admin/album-taxonomies-context";
import { AppPushRouter } from "@common/core/capacitor/app-push-notifications-manager";
import { useNavigate } from "react-router-dom";
import { useDomAssetProxy } from "./hook/use-dom-asset-proxy";
import { ProgressProvider } from "@ui/progress/progress-provider";
import { DeepLinkListener } from "./deep-link-listener";

const mergedConfig = deepMerge(BaseSiteConfig, SiteConfig);

interface Props {
  router: Router;
  onRenderComplete: () => void;
}
export function CommonProvider({ router, onRenderComplete }: Props) {
  useDomAssetProxy();

  useEffect(() => {
    AppBrowser.addListener();

    return () => {
      AppBrowser.removeListener();
    };
  }, []);

  useEffect(() => {
    onRenderComplete();
  }, [onRenderComplete]);

  return (
    <StrictMode>
      <ProgressProvider>
        <DialogStackProvider>
          <QueryClientProvider client={queryClient}>
            <AlbumTaxonomiesProvider>
              <LazyMotion features={domAnimation}>
                <SiteConfigContext.Provider value={mergedConfig}>
                  <ThemeProvider>
                    <CommonRouter router={router} />
                  </ThemeProvider>
                </SiteConfigContext.Provider>
              </LazyMotion>
            </AlbumTaxonomiesProvider>
          </QueryClientProvider>
        </DialogStackProvider>
      </ProgressProvider>
    </StrictMode>
  );
}

async function initAppPushNotificationsManager() {
  await appPushNotificationManager.loadManager();
  await appPushNotificationManager.init();
}

interface CommonRouterProps {
  router: Router;
}
function CommonRouter({ router }: CommonRouterProps) {
  const { require_email_confirmation } = useSettings();
  const { user } = useAuth();

  if (user != null) {
    initAppPushNotificationsManager();
    if (require_email_confirmation && !user.email_verified_at) {
      return (
        <RouterProvider
          router={verifyEmailRouter}
          fallbackElement={<FullPageLoader screen />}
        />
      );
    }
  }

  return (
    <RouterProvider
      router={router}
      fallbackElement={<FullPageLoader screen />}
    />
  );
}

export function RootRoute() {
  const navigate = useNavigate();

  useEffect(() => {
    AppPushRouter.navigate = navigate;
    AppBrowser.setNavigate(navigate);

    return () => {
      AppPushRouter.navigate = undefined;
      AppBrowser.setNavigate(undefined);
    };
  }, [navigate]);

  return (
    <Fragment>
      <DeepLinkListener />
      <ScrollToTop />
      <GlobalTopLoadingBar />
      <Outlet />
      <AppearanceListener />
      <CookieNotice />
      <ToastContainer />
      <StackOutlet />
      <DialogStoreOutlet />
    </Fragment>
  );
}

export function RootErrorElement() {
  const error = useRouteError();

  console.error("Root route error:", error);

  if ((error as any)?.status === 404) {
    return <NotFoundPage />;
  }
  return <PageErrorMessage />;
}

export function GlobalTopLoadingBar() {
  const [bar] = useState(() => new TopProgressBar());
  const { state } = useNavigation();
  const timeoutRef = useRef<any>(null);

  useEffect(() => {
    // react router will always set loading to true when "lazy" is set on route, even if that router is already loaded, this will result in loading bar showing for a few milliseconds
    if (state === "loading") {
      timeoutRef.current = setTimeout(() => {
        bar.show();
      }, 50);
    } else {
      clearTimeout(timeoutRef.current);
      bar.hide();
    }

    return () => {
      if (state !== "loading") {
        clearTimeout(timeoutRef.current);
        bar.hide();
      }
    };
  }, [state, bar]);

  return null;
}

export const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const scrollContainer = document.querySelector("main"); // Ajuste le sélecteur
    if (scrollContainer) {
      scrollContainer.scrollTo(0, 0);
    }
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};
